import { SingleSelectAutocomplete } from "@flat-ui";
import { useGroups } from "@hooks/use-groups";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import defaultGroupThumbnail from "@assets/thumbnails/groups-thumbs/group-thumbnail_default.svg";
import { useEffect, useMemo } from "react";
import { isEmpty } from "es-toolkit/compat";
/**
 * Defines the maximum items to be displayed in the autocomplete menu as suggestions.
 * The number should be low enough to fit them all at once in the screen.
 */
const MAX_ITEMS_TO_DISPLAY = 6;

/**
 * Sorts the options by label.
 */
function sortByLabel(a, b) {
  return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
}

/**
 * The GroupAutoComplete component enables users to select a group from a dynamic list of options
 * based on provided company groups. It offers autocomplete functionality with custom rendering for
 * options and icons, enhancing the user experience in selecting groups effortlessly.
 */
export function GroupAutoComplete({
  callbackOnGroupSelected,
  selectedGroupId,
  placeholder,
  shouldOnlyShowGroupsWithGroupManager = false
}) {
  const companyGroups = useGroups();
  const currentUser = useAppSelector(currentUserSelector);

  /**
   * Reformation company groups data for stellar autocomplete component.
   * Show all group options if the user is a company executive, or shouldOnlyShowGroupsWithGroupManager prop is false.
   * Otherwise, filter the groups to only show those with the role of company manager.
   */
  const groupOptions = useMemo(() => {
    const filteredGroups = currentUser && currentUser.role !== "COMPANY_EXECUTIVE" && shouldOnlyShowGroupsWithGroupManager ? companyGroups.filter(group => group.role === "COMPANY_MANAGER") : companyGroups;
    return filteredGroups.map(group => ({
      title: group.name,
      subtitle: "",
      value: group.id,
      thumbnailImage: isEmpty(group.imageUrl) ? defaultGroupThumbnail : group.imageUrl
    }));
  }, [companyGroups, currentUser, shouldOnlyShowGroupsWithGroupManager]);
  useEffect(() => {
    // Auto-select the only group when nothing is selected yet (e.g. a workspace with a single
    // group). Never override an existing selection, otherwise it silently reassigns the group.
    if (groupOptions.length === 1 && selectedGroupId === undefined) {
      callbackOnGroupSelected(groupOptions[0].value);
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps -- multiple re-renders with the callbackOnGroupSelected
  }, [groupOptions, selectedGroupId]);

  /**
   * Callback fired when the selected option changes.
   * Extracts the group ID from the selected option and notifies the parent.
   */
  function handleAutocompleteChange(value, _reason) {
    if (value && typeof value !== "string") {
      callbackOnGroupSelected(value.value);
    } else {
      callbackOnGroupSelected(undefined);
    }
  }
  const selectedValue = useMemo(() => groupOptions.find(group => group.value === selectedGroupId) ?? null, [groupOptions, selectedGroupId]);
  return <SingleSelectAutocomplete dataTestId="group-auto-complete-test-id" options={groupOptions} onChange={handleAutocompleteChange} sx={{
    width: "100%"
  }} value={selectedValue} isDisabled={groupOptions.length === 1} placeholder={placeholder ?? "Assign this project to a group..."} filterOptions={(options, inputValue) => {
    if (!inputValue) {
      return options.sort(sortByLabel);
    }
    const lowerInputValue = inputValue.toLowerCase();
    return options.filter(option => option.title.toLowerCase().includes(lowerInputValue)).slice(0, MAX_ITEMS_TO_DISPLAY).sort(sortByLabel);
  }} noOptionsText="No groups found!" />;
}